/**
 * Utility functions for working days calculation and work week management
 */

// Default work week configuration (Monday to Friday)
export const DEFAULT_WORK_WEEK = {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false
};

// Day names for UI
export const DAY_NAMES = {
    monday: { short: 'Mo', full: 'Montag' },
    tuesday: { short: 'Di', full: 'Dienstag' },
    wednesday: { short: 'Mi', full: 'Mittwoch' },
    thursday: { short: 'Do', full: 'Donnerstag' },
    friday: { short: 'Fr', full: 'Freitag' },
    saturday: { short: 'Sa', full: 'Samstag' },
    sunday: { short: 'So', full: 'Sonntag' }
};

// Day mapping for JavaScript Date.getDay() (0 = Sunday, 1 = Monday, etc.)
export const DAY_INDEX_MAP = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday'
};

/**
 * Get work week configuration from localStorage or use default
 * @returns {Object} Work week configuration object
 */
export const getWorkWeekConfig = () => {
    try {
        const saved = localStorage.getItem('workWeekConfig');
        return saved ? JSON.parse(saved) : DEFAULT_WORK_WEEK;
    } catch (error) {
        return DEFAULT_WORK_WEEK;
    }
};

/**
 * Save work week configuration to localStorage
 * @param {Object} config - Work week configuration object
 */
export const setWorkWeekConfig = (config) => {
    try {
        localStorage.setItem('workWeekConfig', JSON.stringify(config));
    } catch (error) {
        console.error('Failed to save work week configuration:', error);
    }
};

/**
 * Check if a specific date is a working day based on work week configuration
 * @param {Date} date - Date to check
 * @param {Object} workWeekConfig - Work week configuration (optional, will use saved config if not provided)
 * @returns {boolean} True if the date is a working day
 */
export const isWorkingDay = (date, workWeekConfig = null) => {
    const config = workWeekConfig || getWorkWeekConfig();
    const dayIndex = date.getDay();
    const dayName = DAY_INDEX_MAP[dayIndex];
    return config[dayName] || false;
};

/**
 * Calculate the number of working days between two dates (excluding non-working days)
 * @param {Date} startDate - Start date (inclusive)
 * @param {Date} endDate - End date (inclusive)
 * @param {Object} workWeekConfig - Work week configuration (optional)
 * @returns {number} Number of working days
 */
export const calculateWorkingDays = (startDate, endDate, workWeekConfig = null) => {
    if (!startDate || !endDate || startDate > endDate) {
        return 0;
    }

    const config = workWeekConfig || getWorkWeekConfig();
    let workingDays = 0;
    let currentDate = new Date(startDate);

    // Include both start and end dates in calculation
    while (currentDate <= endDate) {
        if (isWorkingDay(currentDate, config)) {
            workingDays++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return workingDays;
};

/**
 * Calculate end date based on start date and working days duration
 * @param {Date} startDate - Start date
 * @param {number} workingDays - Number of working days
 * @param {Object} workWeekConfig - Work week configuration (optional)
 * @returns {Date} Calculated end date
 */
export const calculateEndDateFromWorkingDays = (startDate, workingDays, workWeekConfig = null) => {
    if (!startDate || workingDays <= 0) {
        return startDate;
    }

    const config = workWeekConfig || getWorkWeekConfig();
    let remainingDays = workingDays;
    let currentDate = new Date(startDate);

    // Start from the start date
    while (remainingDays > 0) {
        if (isWorkingDay(currentDate, config)) {
            remainingDays--;
        }
        if (remainingDays > 0) {
            currentDate.setDate(currentDate.getDate() + 1);
        }
    }

    return currentDate;
};

/**
 * Get CSS class for non-working days styling
 * @param {Date} date - Date to check
 * @param {Object} workWeekConfig - Work week configuration (optional)
 * @returns {string} CSS class name for styling
 */
export const getNonWorkingDayClass = (date, workWeekConfig = null) => {
    return isWorkingDay(date, workWeekConfig) ? '' : 'wx-non-working-day';
};

/**
 * Get all working days of the week as an array
 * @param {Object} workWeekConfig - Work week configuration (optional)
 * @returns {Array} Array of working day names
 */
export const getWorkingDayNames = (workWeekConfig = null) => {
    const config = workWeekConfig || getWorkWeekConfig();
    return Object.keys(config).filter(day => config[day]);
};

/**
 * Convert work week config to display format for UI
 * @param {Object} workWeekConfig - Work week configuration (optional)
 * @returns {string} Display string (e.g., "M-D-M-D-F" for Mon-Tue-Wed-Thu-Fri)
 */
export const workWeekToDisplayString = (workWeekConfig = null) => {
    const config = workWeekConfig || getWorkWeekConfig();
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    return days
        .filter(day => config[day])
        .map(day => DAY_NAMES[day].short)
        .join('-');
};